<template>
	<div class="modal-mask" @click.self="close" @keydown.esc.prevent="close">
		<div
			ref="dialog"
			class="modal-container chat-picker"
			role="dialog"
			aria-modal="true"
			:aria-label="$t('chat.channel_picker_title')"
			tabindex="-1"
		>
			<div class="flex flex-row items-center border-b-default border-border-color">
				<h2 class="flex flex-1 items-center p-4 font-semibold">{{ $t('chat.channel_picker_title') }}</h2>
				<button type="button" class="chat-picker__close mr-4" :aria-label="$t('chat.close')" @click="close">
					<app-icon class="text-2xl text-secondary" icon="cancel" />
				</button>
			</div>
			<ul class="chat-picker__list">
				<li v-for="option in options" :key="option.typeId">
					<button
						type="button"
						class="chat-picker__row"
						:class="`chat-picker__row--${option.typeId}`"
						:aria-label="option.ariaLabel"
						@click="pick(option)"
					>
						<span class="chat-picker__avatar">
							<img
								v-if="imageOf(option)"
								:src="imageOf(option)"
								alt=""
								class="chat-picker__photo"
								@error="onImageError(option)"
							/>
							<chat-icon v-else class="chat-picker__icon" :name="option.icon" />
						</span>
						<span class="chat-picker__text">
							<span class="flex items-center min-w-0">
								<span class="chat-picker__title truncate">{{ option.title }}</span>
								<span v-if="option.tag" class="chat-picker__tag">{{ option.tag }}</span>
							</span>
							<span class="chat-picker__hint">{{ option.hint }}</span>
						</span>
						<span v-if="option.unread > 0" class="chat-picker__badge">{{ option.unreadLabel }}</span>
						<span v-else-if="option.chat" class="chat-picker__continue">{{
							$t('chat.channel_continue')
						}}</span>
						<app-icon v-else class="text-secondary" icon="right" />
					</button>
				</li>
			</ul>
		</div>
	</div>
</template>

<script>
	import AppIcon from '@/components/app/AppIcon';
	import { CHAT_TYPE, chatChannelLabelKey, firstName, unreadBadgeLabel } from '@/services/chat/channels';
	import { findOpenChat, openChat } from '@/services/chat/chatStore';
	import ChatIcon from './ChatIcon.vue';
	import { chatChannelVisual } from './chatVisuals';

	const NOBODY = { name: '', image: '' };

	export default {
		name: 'ChatChannelPicker',
		components: { AppIcon, ChatIcon },
		props: {
			order: {
				type: Object,
				required: true,
			},
		},
		emits: ['close'],
		data: () => ({
			failedImages: {},
		}),
		computed: {
			options() {
				const order = this.order;
				const appOrder = !(order.createdByUser?.id > 0);
				const courier = order.courierUser?.id > 0 ? order.courierUser : null;
				const options = [this.option(CHAT_TYPE.SUPPORT, NOBODY, 'chat.channel_support_hint')];
				const customer = { name: (order.customer?.name || '').trim(), image: '' };
				const customerOption = this.option(CHAT_TYPE.CUSTOMER, customer, 'chat.channel_customer_hint');
				if (appOrder || customerOption.chat) {
					options.push(customerOption);
				}
				const driver = courier ? { name: firstName(courier.name), image: courier.photoUrl || '' } : NOBODY;
				const driverOption = this.option(CHAT_TYPE.DRIVER, driver, 'chat.channel_driver_hint');
				if (courier || driverOption.chat) {
					options.push(driverOption);
				}
				return options;
			},
		},
		methods: {
			option(typeId, orderPerson, hintKey) {
				const chat = this.order.codeId ? findOpenChat({ typeId, orderCode: this.order.codeId }) : null;
				const person = chat?.counterpart?.name ? chat.counterpart : orderPerson;
				const channelLabel = this.$t(chatChannelLabelKey(typeId));
				const unread = chat ? chat.unreadCount : 0;
				const title =
					person.name || (typeId === CHAT_TYPE.SUPPORT ? this.$t('chat.channel_support') : channelLabel);
				const hint = this.$t(hintKey);
				return {
					typeId,
					chat,
					title,
					tag: person.name ? channelLabel : '',
					hint,
					image: person.image || '',
					icon: chatChannelVisual(typeId).icon,
					unread,
					unreadLabel: unreadBadgeLabel(unread),
					ariaLabel: [
						title,
						person.name ? channelLabel : null,
						hint,
						chat ? this.$t('chat.channel_continue') : null,
						unread > 0 ? this.$t('chat.list_unread', { count: unread }) : null,
					]
						.filter(Boolean)
						.join(', '),
				};
			},
			imageOf(option) {
				const photo = option.image && this.failedImages[option.typeId] !== option.image ? option.image : '';
				return photo || chatChannelVisual(option.typeId).face || '';
			},
			onImageError(option) {
				this.failedImages = { ...this.failedImages, [option.typeId]: option.image };
			},
			pick(option) {
				const popupKey = openChat({ typeId: option.typeId, orderCode: this.order.codeId });
				this.close();
				if (!popupKey) {
					this.$notificationService.error(this.$t('chat.unavailable'));
				}
			},
			close() {
				this.$emit('close');
			},
		},
		mounted() {
			this.$refs.dialog.focus();
		},
	};
</script>

<style lang="scss" scoped>
	$chat-support-tint: #cdf4d8;
	$chat-customer-tint: #ffd1dc;
	$chat-driver-tint: #cde1fe;

	.chat-picker {
		width: 420px;
		max-width: calc(100vw - 32px);
		outline: none;
	}

	.chat-picker__close {
		display: flex;
		align-items: center;
		background: transparent;
		border: 0;
		cursor: pointer;
	}

	.chat-picker__list {
		display: flex;
		flex-direction: column;
		gap: 8px;
		padding: 12px;
		max-height: 70vh;
		overflow-y: auto;
	}

	.chat-picker__row {
		--chat-accent: var(--success);
		--chat-tint: #{$chat-support-tint};
		display: flex;
		align-items: center;
		gap: 12px;
		width: 100%;
		padding: 10px 12px;
		text-align: left;
		background: var(--white);
		border: 1px solid var(--border-color);
		border-radius: 12px;
		cursor: pointer;
		transition: background-color 150ms ease, border-color 150ms ease;

		&:hover,
		&:focus-visible {
			@apply bg-gray-100;
			border-color: var(--chat-accent);
			outline: none;
		}
	}

	.chat-picker__row--5 {
		--chat-accent: var(--primary);
		--chat-tint: #{$chat-customer-tint};
	}

	.chat-picker__row--8 {
		--chat-accent: var(--info);
		--chat-tint: #{$chat-driver-tint};
	}

	.chat-picker__avatar {
		display: flex;
		flex-shrink: 0;
		align-items: center;
		justify-content: center;
		width: 44px;
		height: 44px;
		overflow: hidden;
		background: var(--chat-tint);
		border-radius: 9999px;
	}

	.chat-picker__photo {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	.chat-picker__icon {
		width: 24px;
		height: 24px;
		fill: var(--chat-accent);
	}

	.chat-picker__text {
		display: flex;
		flex: 1;
		flex-direction: column;
		min-width: 0;
	}

	.chat-picker__title {
		font-size: 16px;
		font-weight: 700;
		line-height: 1.25;
		color: var(--black);
	}

	.chat-picker__tag {
		flex-shrink: 0;
		margin-left: 6px;
		padding: 1px 7px;
		font-size: 10px;
		font-weight: 700;
		line-height: 14px;
		letter-spacing: 0.08em;
		text-transform: uppercase;
		color: var(--chat-accent);
		background: var(--chat-tint);
		border-radius: 10px;
	}

	.chat-picker__hint {
		margin-top: 2px;
		font-size: 13px;
		line-height: 1.3;
		color: var(--muted);
	}

	.chat-picker__badge {
		flex-shrink: 0;
		min-width: 22px;
		height: 22px;
		padding: 0 6px;
		font-size: 12px;
		font-weight: 700;
		line-height: 22px;
		text-align: center;
		color: var(--white);
		background: var(--primary);
		border-radius: 9999px;
		font-variant-numeric: tabular-nums;
	}

	.chat-picker__continue {
		flex-shrink: 0;
		font-size: 13px;
		font-weight: 700;
		color: var(--chat-accent);
	}
</style>
